/**
 * makeDocxFromJson.js
 *
 * Word (.docx) export — parallel to MakeDocDefinitionFromJson.js.
 * Accepts the same { report, rakennusData, ... } params and returns a
 * docx.Document ready for Packer.toBlob().
 *
 * Requires:  npm install docx  (already installed as docx@9)
 */
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  WidthType,
  BorderStyle,
  AlignmentType,
  PageBreak,
  Header,
} from 'docx';

import { renderBulletListBlockDocx } from '../report/blocksDocx/renderBulletListBlockDocx';
import { renderTextAreaDocx } from '../report/blocksDocx/renderTextAreaDocx';
import { renderLexicalTextareaDocx } from '../report/blocksDocx/renderLexicalTextareaDocx';
import { renderSubsectionListBlockDocx } from '../report/blocksDocx/renderSubsectionListBlockDocx';
import { renderRiskBlockDocx } from '../report/blocksDocx/renderRiskBlockDocx';
import { renderPropertyInfoDocx } from '../report/blocksDocx/renderPropertyInfoDocx';
import { renderSignatureBlockDocx } from '../report/blocksDocx/renderSignatureBlockDocx';
import { renderImageBlockDocx } from '../report/blocksDocx/renderImageBlockDocx';
import { renderSubsectionDocx } from '../report/blocksDocx/renderSubsectionDocx';
import { renderKokonaisKuntoarvioDocx } from '../report/blocksDocx/renderKokonaisKuntoarvioDocx';
import { renderToimeksiantajaBlockDocx } from '../report/blocksDocx/renderToimeksiantajaBlockDocx';
import { renderToimenpideEhdotuksetDocx } from '../report/blocksDocx/renderToimenpideEhdotuksetDocx';
import { renderYhteenvetoToimenpideBlockDocx } from '../report/blocksDocx/renderYhteenvetoToimenpideBlockDocx';
import { renderTeamBlockDocx } from '../report/blocksDocx/renderTeamBlockDocx';
import { renderYmparistoBlockDocx } from '../report/blocksDocx/renderYmparistoBlockDocx';
import { renderEnergiaTableBlockDocx } from '../report/blocksDocx/renderEnergiaTableBlockDocx';
import { renderTekniikkatauluBlockDocx } from '../report/blocksDocx/renderTekniikkatauluBlockDocx';
import { renderTutkimustarpeetTauluBlockDocx } from '../report/blocksDocx/renderTutkimustarpeetTauluBlockDocx';
import { renderPTSBlockDocx } from '../report/blocksDocx/renderPTSBlockDocx';
import { renderMapImageDocx } from '../report/blocksDocx/renderMapImageDocx';
import {
  renderWeatherBlockDocx
} from "../report/blocksDocx/renderWeatherBlockDocx";
import { getEnergiaTablesByKiinteisto } from '../../api/api';

// ─── shared style helpers ────────────────────────────────────────────────────

const FONT = 'Arial';

const NONE_BORDER = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
const NO_BORDERS = { top: NONE_BORDER, bottom: NONE_BORDER, left: NONE_BORDER, right: NONE_BORDER };
const TABLE_NO_BORDERS = { ...NO_BORDERS, insideH: NONE_BORDER, insideV: NONE_BORDER };

const COLOR = {
  green: '04aa00',
  sectionGreen: '198754',
  headerBg: '04aa00',
};

// ─── utilities ───────────────────────────────────────────────────────────────

function parseContent(content) {
  try { return JSON.parse(content); } catch { return { blocks: [] }; }
}

// ─── main export ─────────────────────────────────────────────────────────────

/**
 * Build a docx Document from the same parameters as MakeDocDefinitionFromJson.
 * @returns {Promise<Document>}
 */
export default async function makeDocxFromJson({
  report,
  rakennusData,
  mergedRakennusData,
  riskidata,
  assignments,
  groupedToimenpiteet,
  ptsContext,
}) {
  if (!report) throw new Error('Report data is missing');

  const children = [];
  const sectionImageCounters = {};
  const subsectionCounters = {};

  let sectionCounter = 0;
  let tocInserted = false;

  // Fetch energia blocks (mirrors MakeDocDefinitionFromJson)
  let energiaBlocks = [];
  try {
    const energiaRes = await getEnergiaTablesByKiinteisto(report.id_kiinteisto);
    if (energiaRes.data?.length > 0) {
      energiaBlocks = energiaRes.data[0]?.data?.blocks || [];
    }
  } catch (err) {
    console.warn('DOCX: Failed to fetch energia tables:', err);
  }

  // Sorted sections (mirror MakeDocDefinitionFromJson)
  let sections = Array.isArray(report.report_sections) ? [...report.report_sections] : [];
  if (Array.isArray(report.section_order) && report.section_order.length > 0) {
    sections.sort((a, b) => report.section_order.indexOf(a.id) - report.section_order.indexOf(b.id));
  }

  // ── renderBlocksDocx — dispatches each block type to its docx renderer ──────
  const renderBlocksDocx = async (blocks, target, sectionNumber, level = 2) => {
    for (const block of blocks) {
      const { type, data } = block;

      switch (type) {

        case 'subsection':
          await renderSubsectionDocx({
            block, children: target, subsectionCounters, sectionNumber, level, renderBlocksDocx,
          });
          break;

        case 'subsection2': {
          const innerChildren = [];
          if (Array.isArray(data?.blocks)) {
            await renderBlocksDocx(data.blocks, innerChildren, sectionNumber, level + 1);
          }
          if (innerChildren.length) {
            subsectionCounters[sectionNumber] = (subsectionCounters[sectionNumber] || 0) + 1;
            const subNum = `${sectionNumber}.${subsectionCounters[sectionNumber]}`;
            target.push(
              new Paragraph({
                children: [new TextRun({ text: `${subNum} ${data?.title || 'Aliosio'}`, bold: true, size: 26, font: FONT })],
                spacing: { before: 200, after: 100 },
              }),
              ...innerChildren
            );
          }
          break;
        }

        case 'LexicalTextarea':
          renderLexicalTextareaDocx({ data, children: target });
          break;

        case 'textarea':
          renderTextAreaDocx({ data, children: target });
          break;

        case 'BulletListBlock':
          renderBulletListBlockDocx({ children: target, items: data?.items });
          break;

        case 'RiskBlock':
          renderRiskBlockDocx({ children: target, riskidata });
          break;

        case 'propertyInfo':
          renderPropertyInfoDocx({ block, children: target, rakennusData, mergedRakennusData });
          break;

        case 'SignatureBlock':
          await renderSignatureBlockDocx({ data, children: target });
          break;

        case 'SubsectionListBlock':
          renderSubsectionListBlockDocx({ data, children: target });
          break;

        case 'ImageBlock':
          await renderImageBlockDocx({ data, children: target, rakennusData, sectionImageCounters, sectionNumber });
          break;

        case 'KokonaisKuntoarvio':
          renderKokonaisKuntoarvioDocx(data, target);
          break;

        case 'ToimeksiantajaBlock':
          renderToimeksiantajaBlockDocx({ data, children: target });
          break;

        case 'ToimenpideEhdotukset':
          renderToimenpideEhdotuksetDocx({ data, children: target });
          break;

        case 'YhteenvetoToimenpideBlock':
          renderYhteenvetoToimenpideBlockDocx({ children: target, groupedToimenpiteet });
          break;

        case 'TeamBlockNimikkeella':
        case 'teamBlockYrityksella':
          renderTeamBlockDocx({ data, children: target, assignments, report });
          break;

        case 'YmparistoBlock':
          renderYmparistoBlockDocx({ block, children: target, rakennusData });
          break;

        case 'MapBlock2':
        case 'MapImage':
          await renderMapImageDocx({ children: target, rakennusData, mergedRakennusData, zoom: 17 });
          break;

        case 'PTSBlock':
        case 'PtsBlockYhteenveto':
        case 'PtsBlockTalous':
          await renderPTSBlockDocx(data, target, report);
          break;

        case 'PtsBlockTutkimukset':
          renderTutkimustarpeetTauluBlockDocx({ ptsContext, children: target });
          break;

        case 'PtsBlockRakennetekniikka':
          renderTekniikkatauluBlockDocx({ type: 'Rakennetekniikka', ptsContext, children: target });
          break;

        case 'PtsBlockLVI':
          renderTekniikkatauluBlockDocx({ type: 'LVI Järjestelmät', ptsContext, children: target });
          break;

        case 'PtsBlockSahko':
          renderTekniikkatauluBlockDocx({ type: 'Sähköjärjestelmät', ptsContext, children: target });
          break;
        case "WeatherBlock":
          renderWeatherBlockDocx({
            data,
            children: target,
          });
          break;
        case 'EnergiaBlockHeat':
        case 'EnergiaBlockWater':
        case 'EnergiaBlockElectricity': {
          const indexMap = { EnergiaBlockHeat: 0, EnergiaBlockWater: 1, EnergiaBlockElectricity: 2 };
          const energiaData = energiaBlocks[indexMap[type]];
          if (energiaData) {
            renderEnergiaTableBlockDocx({ data: energiaData, children: target });
          }
          break;
        }

        case 'coverPageForm':
          // Handled at the section level below
          break;

        default:
          break;
      }
    }
  };

  // ── Section loop ─────────────────────────────────────────────────────────────
  for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
    const section = sections[sectionIndex];
    const sectionName = (section.name || `Osa ${sectionIndex + 1}`).toUpperCase();
    const isCoverPage = sectionName.toLowerCase() === 'kansisivu';

    const parsed = parseContent(section.content);
    const blocks = parsed.blocks || [];

    if (isCoverPage) {
      // ── Cover page: simple title block ─────────────────────────────────────
      const coverBlock = blocks.find(b => b.type === 'coverPageForm');
      const title = coverBlock?.data?.title || report.name || 'Raportti';
      const dateIso = coverBlock?.data?.dateIso || report.created_at;
      const dateStr = dateIso ? new Date(dateIso).toLocaleDateString('fi-FI') : new Date().toLocaleDateString('fi-FI');

      children.push(
        new Paragraph({ children: [new TextRun({ text: title, bold: true, size: 56, font: FONT })], spacing: { after: 200 } }),
        new Paragraph({ children: [new TextRun({ text: 'INSINÖÖRITOIMISTO WAATIVA', size: 22, font: FONT })], spacing: { after: 80 } }),
        new Paragraph({ children: [new TextRun({ text: `Tarkastuspäivämäärä: ${dateStr}`, size: 22, font: FONT })], spacing: { after: 400 } })
      );
      continue;
    }

    // ── Normal section heading ─────────────────────────────────────────────────
    sectionCounter++;

    // Skip counter 2 when TOC was inserted (mirrors PDF behaviour)
    if (sectionCounter === 2 && tocInserted) sectionCounter++;

    subsectionCounters[sectionCounter] = 0;

    const headingChildren = [];
    if (children.length > 0) headingChildren.push(new PageBreak());
    headingChildren.push(
      new TextRun({ text: `${sectionCounter}\u2003${sectionName}`, bold: true, size: 32, font: FONT })
    );

    children.push(
      new Paragraph({
        children: headingChildren,
        border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: COLOR.sectionGreen } },
        spacing: { before: 240, after: 160 },
      })
    );

    // ── Render blocks ──────────────────────────────────────────────────────────
    await renderBlocksDocx(blocks, children, sectionCounter);
  }

  // ── Assemble Document with green page header ──────────────────────────────────
  const dateText = new Date().toLocaleDateString('fi-FI');
  const propText = rakennusData?.kohteen_nimi || report.name || '';

  return new Document({
    styles: {
      default: {
        document: { run: { font: FONT, size: 22 } },
      },
    },
    sections: [
      {
        properties: {
          page: {
            margin: { top: 1134, right: 1134, bottom: 1134, left: 1134 }, // ~2 cm
          },
        },
        headers: {
          default: new Header({
            children: [
              new Table({
                rows: [
                  new TableRow({
                    children: [
                      new TableCell({
                        children: [new Paragraph({ children: [new TextRun({ text: dateText, color: 'FFFFFF', bold: true, size: 20 })] })],
                        shading: { fill: COLOR.headerBg },
                        borders: NO_BORDERS,
                        width: { size: 20, type: WidthType.PERCENTAGE },
                      }),
                      new TableCell({
                        children: [new Paragraph({
                          children: [new TextRun({ text: propText, color: 'FFFFFF', bold: true, size: 20 })],
                          alignment: AlignmentType.RIGHT,
                        })],
                        shading: { fill: COLOR.headerBg },
                        borders: NO_BORDERS,
                        width: { size: 80, type: WidthType.PERCENTAGE },
                      }),
                    ],
                  }),
                ],
                width: { size: 100, type: WidthType.PERCENTAGE },
                borders: TABLE_NO_BORDERS,
              }),
            ],
          }),
        },
        children,
      },
    ],
  });
}

/**
 * Generate and trigger a browser download of the .docx file.
 *
 * @param {Object} params  Same params as makeDocxFromJson
 * @param {string} [filename='raportti.docx']
 */
export async function downloadDocx(params, filename = 'raportti.docx') {
  const doc = await makeDocxFromJson(params);
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
